import {Component, Input, Output, EventEmitter, Inject, OnInit, OnChanges} from '@angular/core';
import {isDefined} from '../../utils/utils';
import {Browser, compareMaybeNumeric} from '../../types/browser';
import {FeatureService} from '@webmate-web-core/shared/services/feature.service';
import * as _ from 'lodash';
import {LogoResolverService} from '@webmate-web-core/shared/services/logo-resolver.service';
import {Subscription} from 'rxjs';
import {Platform, platformComparator} from '../../types/platform';

@Component({
    selector: 'wm-browserchooser',
    templateUrl: './browserchooser.component.html',
    styleUrls: ['./browserchooser.component.less'],
})
export class BrowserChooserComponent implements OnChanges {

    // Can be overwritten by feature
    private DEFAULT_WIN = "WINDOWS 11";
    private DEFAULT_MAC = "MACOS MONTEREY";

    //TODO: find a better solution to represent this
    private VM_MODEL = 'vm';

    constructor(private logoResolverService: LogoResolverService, private featureService: FeatureService) {
        featureService.getFeature("webmate.frontend.default-browser-os").subscribe(f => {
            if (isDefined(f) && f.isActive) {
                if (isDefined(f['windows'])) this.DEFAULT_WIN = f['windows'];
                if (isDefined(f['mac'])) this.DEFAULT_MAC = f['mac'];
            }
        });
    }

    activeBrowsers: Set<Browser> = new Set<Browser>();

    /**
     * List of browser available for selection.
     */
    @Input() browsers: Browser[] = [];

    /**
     * Optional Browser to set during initialization
     */
    @Input() initWithBrowser: Browser = undefined;

    /**
     *  Can be blue
     */
    style: string = 'blue';

    @Input() closeDetailsOnVersionSelect: boolean = true;

    @Input() simpleMode = false;

    @Input() deployedBrowsers?: Browser[];

    @Input() clear?: EventEmitter<void>;

    @Input() filterLocale: boolean = false;

    /**
     * Event is emitted if multiSelect is false and the user chooses a Browser.
     */
    @Output('browserChosen') browserChosenEvent = new EventEmitter<Browser>();

    browserStructure = {};
    deployedVersions = {};
    deployedPlatforms = {};
    deployedArchetypes = {};

    displayedArchetypes: string[] = [];
    displayedModels: string[] = [];
    recalculateModels = true;
    displayedPlatforms: string[] = [];
    recalculatePlatforms = true;

    selectedBrowserByArchetype = {};
    selectedArchetype: string;
    selectedPlatform: string = '';
    selectedModel: string = this.VM_MODEL;
    selectedVersion: string = '';
    mobileBrowsers = undefined;
    detailsShown: boolean = false;

    platformLogos = {};
    modelLogos = {};
    browserLogos = {};

    showDeployed = false;

    clearSubscription: Subscription;

    //onChanges instead of onInit, as the browsers may change and the structure has to be rebuilt
    ngOnChanges() {
        if (this.clearSubscription) {
            this.clearSubscription.unsubscribe();
        }
        if (isDefined(this.clear)) {
            this.clearSubscription = this.clear.subscribe(() => {
                this.selectedArchetype = undefined;
                this.selectedPlatform = '';
                this.selectedVersion = '';
                this.detailsShown = false;
            });
        }
        if (isDefined(this.deployedBrowsers)) {
            this.deployedBrowsers = this.deployedBrowsers.filter(b => b.capabilities?.['selenium.webdriverSupport'] ?? false);
        }
        this.showDeployed = isDefined(this.deployedBrowsers) && this.deployedBrowsers.length > 0;

        this.browsers = this.browsers.sort((a, b) => a.browserType.localeCompare(b.browserType));

        this.browserStructure = {};
        this.selectedBrowserByArchetype = {};

        this.browsers.filter(b => b.capabilities?.['selenium.webdriverSupport'] ?? false).forEach(b => {
            if (!(b.browserType in this.browserStructure)) {
                this.browserStructure[b.browserType] = {};
            }

            let model = b.model;
            if (!isDefined(b.model)) {
                model = this.VM_MODEL;
            }

            if (!(model in this.browserStructure[b.browserType])) {
                this.browserStructure[b.browserType][model] = {};
            }

            if (!(b.platform.getFullName() in this.browserStructure[b.browserType][model])) {
                this.browserStructure[b.browserType][model][b.platform.getFullName()] = {};
            }

            this.browserStructure[b.browserType][model][b.platform.getFullName()][b.version] = b;

            if (this.showDeployed) {
                if (isDefined(this.deployedBrowsers.find(bb => bb.equalsOtherBrowser(b)))) {
                    if (!isDefined(this.deployedVersions[b.browserType])) this.deployedVersions[b.browserType] = {};
                    if (!isDefined(this.deployedVersions[b.browserType][model])) this.deployedVersions[b.browserType][model] = {};
                    if (!isDefined(this.deployedVersions[b.browserType][model][b.platform.getFullName()])) this.deployedVersions[b.browserType][model][b.platform.getFullName()] = {};
                    this.deployedVersions[b.browserType][model][b.platform.getFullName()][b.version] = true;

                    if (!isDefined(this.deployedPlatforms[b.browserType])) this.deployedPlatforms[b.browserType] = {};
                    if (!isDefined(this.deployedPlatforms[b.browserType][model])) this.deployedPlatforms[b.browserType][model] = {};
                    this.deployedPlatforms[b.browserType][model][b.platform.getFullName()] = true;

                    this.deployedArchetypes[b.browserType] = true;
                }
            }
            if (!(b.platform.getFullName() in this.platformLogos)) {
                this.platformLogos[b.platform.getFullName()] = '';
                this.logoResolverService.getLogoForPlatform(b.platform).subscribe(logo => this.platformLogos[b.platform.getFullName()] = logo);
            }
            if (!(b.model in this.modelLogos)) {
                this.modelLogos[b.model] = '';
                this.logoResolverService.getLogoForModel(undefined, model).subscribe(logo => this.modelLogos[model] = logo);
            }
        });
        this.modelLogos[this.VM_MODEL] = '/assets/img/generic_desktop.png';

        // Set default versions for all archetypes
        for (let a of this.getBrowserArchetypes()) {
            if (isDefined(this.selectedBrowserByArchetype[a])) {
                continue;
            }
            if (this.showDeployed && this.getDeployedForArchetype(a).length > 0) {
                // we should show deployed browsers and there is a machine deployed for this archetype
                let bs = this.getDeployedForArchetype(a);
                if (bs.length === 1) {
                    this.selectedBrowserByArchetype[a] = bs[0];
                } else {
                    // select a win template with the highest browser version if existing
                    let win = bs.filter(b => b.platform.getFullName().indexOf(this.DEFAULT_WIN) !== -1).sort((a, b) => compareMaybeNumeric(a.version, b.version)).filter(b => !b.version.includes('fake')).slice(-1)[0];
                    if (isDefined(win)) {
                        this.selectedBrowserByArchetype[a] = win;
                    } else {
                        let mac = bs.filter(b => b.platform.getFullName().indexOf(this.DEFAULT_MAC) !== -1).sort((a, b) => compareMaybeNumeric(a.version, b.version)).filter(b => !b.version.includes('fake')).slice(-1)[0];
                        if (isDefined(mac)) {
                            this.selectedBrowserByArchetype[a] = mac;
                        } else {
                            // there is neither a win 10 nor a Mac HighSierra. Just use the one with the highest browser version
                            const sorted = bs.sort((a, b) => compareMaybeNumeric(a.version, b.version)).filter(b => !b.version.includes('fake'));
                            this.selectedBrowserByArchetype[a] = sorted[sorted.length - 1];
                        }
                    }
                }
            } else {
                // show regular default
                let allModels = this.getModelsForArchetype(a);
                let selectedModel = allModels.indexOf(this.VM_MODEL) != -1 ? this.VM_MODEL : allModels[0];
                let allOS = this.getPlatformsForArchetypeAndModel(a, selectedModel);
                let win_index = allOS.indexOf(this.DEFAULT_WIN);
                let mac_index = allOS.indexOf(this.DEFAULT_MAC);
                let selectedOs = allOS[win_index !== -1 ? win_index : (mac_index !== -1 ? mac_index : allOS.length - 1)];
                let selectedVersion = this.getVersionsForArchetypeModelAndPlatform(a, selectedModel, selectedOs).filter(b => !b.includes('fake')).slice(-1)[0];
                this.selectedBrowserByArchetype[a] = this.browserStructure[a][selectedModel][selectedOs][selectedVersion];
            }
            this.logoResolverService.getLogoForBrowser(this.selectedBrowserByArchetype[a]).subscribe(logo => this.browserLogos[a] = logo);
        }

        if (this.initWithBrowser) {
            this.selectedArchetype = this.initWithBrowser.browserType;
            this.selectedPlatform = this.initWithBrowser.platform.getFullName();
            this.selectedVersion = this.initWithBrowser.version;
            this.selectedBrowserByArchetype[this.selectedArchetype] = this.initWithBrowser;
        }
    }

    getDeployedForArchetype(a: string): Browser[] {
        return (this.deployedBrowsers || []).filter(b => b.browserType === a);
    }

    getBrowserArchetypes(): string[] {
        return Object.keys(this.browserStructure);
    }

    getModelsForArchetype(archetype: string, display: boolean = false): string[] {
        if (!this.recalculateModels && this.selectedArchetype === archetype) {
            return this.displayedModels;
        }

        if (archetype in this.browserStructure) {
            const models = Object.keys(this.browserStructure[archetype])
                .sort((a, b) => {
                    if (a === this.VM_MODEL) {
                        return -1;
                    } else if (b === this.VM_MODEL) {
                        return 1;
                    } else {
                        return a.localeCompare(b);
                    }
                });

            if (display) {
                this.recalculateModels = false;
                this.displayedModels = models;
            }

            return models;
        }
        return [];
    }

    getPlatformsForArchetypeAndModel(archetype: string, model: string, display: boolean = false): string[] {
        if (!this.recalculatePlatforms && this.selectedArchetype === archetype && this.selectedModel === model) {
            return this.displayedPlatforms;
        }

        if (archetype in this.browserStructure && model in this.browserStructure[archetype]) {
            const platforms = Object.keys(this.browserStructure[archetype][model])
                .sort((platformA, platformB) => {
                    return platformComparator(
                        new Platform(this.getPlatformType(platformA), this.getPlatformVersion(platformA)),
                        new Platform(this.getPlatformType(platformB), this.getPlatformVersion(platformB)));
                });
            if (display) {
                this.displayedPlatforms = platforms;
                this.recalculatePlatforms = false;
            }
            return platforms;
        }
        return [];
    }

    cropPlatform(os: string) {
        return os.split(' ').slice(1).join(' ');
    }

    getPlatformType(os: string) {
        return os.split(' ')[0];
    }

    getPlatformVersion(os: string) {
        return os.split(' ').slice(1, 2).join(' ');
    }

    getVersionsForArchetypeModelAndPlatform(archetype: string, model: string, os: string) {
        if (isDefined(this.browserStructure[archetype])
            && isDefined(this.browserStructure[archetype][model])
            && isDefined(this.browserStructure[archetype][model][os])) {
            return Object.keys(this.browserStructure[archetype][model][os]);
        }
        return [];
    }

    toggleDetails(archetype: string) {
        if (this.selectedArchetype === archetype) {
            this.detailsShown = !this.detailsShown;
        } else {
            this.selectArchetype(archetype);
            this.detailsShown = true;
        }
    }

    selectArchetype(archetype: string) {
        this.selectedArchetype = archetype;
        this.recalculateModels = true;
        this.recalculatePlatforms = true;

        if (this.showDeployed) {
            const sorted = this.getDeployedForArchetype(this.selectedArchetype).sort((a, b) => compareMaybeNumeric(a.version, b.version));
            let bs = sorted[sorted.length - 1];
            if (isDefined(bs)) {
                this.selectPlatform(bs.platform.getFullName());
                return;
            }
        }

        let defaultBrowser = this.selectedBrowserByArchetype[this.selectedArchetype] as Browser;
        if (isDefined(defaultBrowser.model)) {
            this.selectModel(defaultBrowser.model);
        } else {
            this.selectedModel = this.VM_MODEL;
            this.selectPlatform(defaultBrowser.platform.getFullName());
        }
    }

    selectModel(model: string) {
        this.selectedModel = model;
        this.recalculatePlatforms = true;

        if (this.showDeployed) {
            const sorted = this.getDeployedForArchetype(this.selectedArchetype)
                .filter(b => b.model === model && b.platform.getFullName() == this.selectedPlatform)
                .sort((a, b) => compareMaybeNumeric(a.version, b.version));
            let bs = sorted[sorted.length - 1];
            if (isDefined(bs)) {
                this.selectPlatform(bs.platform.getFullName());
                return;
            }
        }

        this.selectPlatform(Object.keys(this.browserStructure[this.selectedArchetype][model])[0]);
    }

    clickPlatform(os: string) {
        if (this.selectedPlatform !== os) {
            this.selectPlatform(os);
        }
    }

    selectPlatform(os: string) {
        this.selectedPlatform = os;

        if (this.showDeployed) {
            const sorted = this.getDeployedForArchetype(this.selectedArchetype)
                .filter(b => b.platform.getFullName() === os)
                .sort((a, b) => compareMaybeNumeric(a.version, b.version))
                .filter(b => !b.version.includes('fake'));
            let bs = sorted[sorted.length - 1];
            if (isDefined(bs)) {
                this.selectVersion(bs.version);
                return;
            }
        }
        const versions = this.getVersionsForArchetypeModelAndPlatform(this.selectedArchetype, this.selectedModel, os).filter(b => !b.includes('fake'));
        const version = versions.slice(-1)[0];
        this.selectVersion(version);
    }

    selectVersion(version: string) {
        this.selectedVersion = version;
        this.selectedBrowserByArchetype[this.selectedArchetype] = this.browserStructure[this.selectedArchetype][this.selectedModel][this.selectedPlatform][this.selectedVersion];
        this.chooseBrowser(this.selectedBrowserByArchetype[this.selectedArchetype]);
    }

    clickVersion(version: string) {
        if (this.selectedVersion !== version) {
            this.selectVersion(version);
        }
        if (this.closeDetailsOnVersionSelect) {
            this.toggleDetails(this.selectedArchetype);
        }
    }

    needsModel(): boolean {
        return isDefined(this.selectedArchetype) && Object.keys(this.browserStructure[this.selectedArchetype]).length > 1;
    }

    getNiceModelName(model: string): string {
        if (model === this.VM_MODEL) {
            return 'VM';
        } else {
            return model;
        }
    }

    chooseBrowser(browser: Browser) {
        if (this.filterLocale) {
            let filteredBrowser = _.cloneDeep(browser);
            if (filteredBrowser.capabilities) {
                delete filteredBrowser.capabilities["browser.locale"];
                delete filteredBrowser.capabilities["browser.language"];
            }
            this.browserChosenEvent.emit(filteredBrowser);
        } else {
            this.browserChosenEvent.emit(browser);
        }
    }

    toggleBrowser(browser: Browser) {
        this.activeBrowsers = new Set<Browser>([browser]);
        this.browserChosenEvent.emit(this.activeBrowsers.values().next().value);
    }

    /**
     * Returns whether Browser is contained in selected browsers.
     */
    browserSelected(browser: Browser) {
        return this.activeBrowsers.has(browser);
    }

    getBrowser(archetype: string, platform: string, version) {
        return this.browserStructure[archetype][platform][version];
    }

    isVersionDeployed(archetype: string, platform: string, version, model?) {
        let m = isDefined(model) ? model : this.VM_MODEL;
        return isDefined(this.deployedVersions[archetype]) && isDefined(this.deployedVersions[archetype][m]) && isDefined(this.deployedVersions[archetype][m][platform]) && isDefined(this.deployedVersions[archetype][m][platform][version]);
    }

    isPlatformDeployed(archetype: string, platform: string) {
        return isDefined(this.deployedPlatforms[archetype]) && isDefined(this.deployedPlatforms[archetype][platform]);
    }

    isArchetypeDeployed(archetype: string) {
        return isDefined(this.deployedArchetypes[archetype]);
    }

}
